import { useCallback } from 'react';
import { stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityDisplayName,
  entityRouteParams,
  entityRouteRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import {
  Alert,
  Box,
  ButtonIcon,
  Flex,
  Link,
  Skeleton,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import { RiStarFill } from '@remixicon/react';
import { useAsyncValue } from './useAsyncValue';
import { HomeCard } from './HomeCard';

const FIELDS = [
  'kind',
  'metadata.namespace',
  'metadata.name',
  'spec.type',
  'metadata.title',
  'spec.profile.displayName',
];

/**
 * The home page "Your Starred Entities" card on BUI, in place of
 * @backstage/plugin-home's Material-UI HomePageStarredEntities: the same list
 * of starred entities sorted by title, each with an unstar button, a link to
 * the entity and its "kind — type" underneath.
 */
export const StarredEntitiesCard = () => {
  const catalogApi = useApi(catalogApiRef);
  const entityRoute = useRouteRef(entityRouteRef);
  const { starredEntities, toggleStarredEntity } = useStarredEntities();

  const loadEntities = useCallback(async () => {
    if (!starredEntities.size) return [];
    const { items } = await catalogApi.getEntitiesByRefs({
      entityRefs: [...starredEntities],
      fields: FIELDS,
    });
    return items.filter((e): e is Entity => !!e);
  }, [catalogApi, starredEntities]);
  const entities = useAsyncValue(loadEntities);

  let content;
  if (starredEntities.size === 0) {
    content = (
      <Text variant="body-large">
        Click the star beside an entity name to add it to this list!
      </Text>
    );
  } else if (entities.loading) {
    content = (
      <Flex direction="column" gap="2" role="progressbar" aria-label="Loading">
        <Skeleton width="100%" height={16} rounded />
        <Skeleton width="60%" height={16} rounded />
      </Flex>
    );
  } else if (entities.error) {
    content = (
      <Alert
        status="danger"
        icon
        title="Could not load your starred entities"
        description={entities.error.message}
      />
    );
  } else {
    const sorted = [...(entities.value ?? [])].sort((a, b) =>
      (a.metadata.title ?? a.metadata.name).localeCompare(
        b.metadata.title ?? b.metadata.name,
      ),
    );
    content = (
      <Flex direction="column" gap="1">
        {sorted.map(entity => {
          const type =
            entity.spec && 'type' in entity.spec
              ? String(entity.spec.type).toLowerCase()
              : undefined;
          const secondary = [entity.kind.toLowerCase(), type]
            .filter(Boolean)
            .join(' — ');
          return (
            <Flex key={stringifyEntityRef(entity)} align="center" gap="2">
              <TooltipTrigger delay={300}>
                <ButtonIcon
                  size="small"
                  variant="tertiary"
                  aria-label="Remove entity from favorites"
                  icon={<RiStarFill />}
                  onPress={() => toggleStarredEntity(entity)}
                  style={{ color: 'var(--bui-fg-warning)' }}
                />
                <Tooltip>Remove entity from favorites</Tooltip>
              </TooltipTrigger>
              <Box style={{ minWidth: 0 }}>
                <Link
                  href={entityRoute(entityRouteParams(entity))}
                  standalone
                  variant="body-medium"
                  style={{ color: 'var(--bui-fg-primary)' }}
                >
                  <EntityDisplayName hideIcon entityRef={entity} />
                </Link>
                <Text
                  as="p"
                  variant="body-small"
                  color="secondary"
                  style={{ textTransform: 'uppercase' }}
                >
                  {secondary}
                </Text>
              </Box>
            </Flex>
          );
        })}
      </Flex>
    );
  }

  return <HomeCard title="Your Starred Entities">{content}</HomeCard>;
};
